/**
 * vm-fit-finder.js: size guide behaviour (sizing). <vm-size-guide> in sections/vm-size-guide.liquid.
 *
 * - Fit finder: men's/women's (picks the product line), product, usual US size or a measurement
 *   (in/cm), fit, hoodie -> recommended US size, the tag size, a reason, and a link that opens the
 *   PDP on that size. Men's and women's products are separate (28 Sep 2026), so there is no
 *   conversion: a women's M picks the Women's M. Sizes come from the rendered tables
 *   (data-us/-tag/-min/-max on each row), so the answer always matches the chart on screen.
 *   Logic: site/size-guide.json fit_finder.logic (P0; recalibrate after the fit trial).
 * - The gender answer also switches the charts to the Men's or Women's group; ?sizes=women or
 *   ?sizes=men (the PDP size drawer's guide link) preselects both.
 * - Keeps the finder's in/cm toggle and the charts' in/cm toggle in step (the charts switch
 *   by CSS :has, so they work without JS).
 * - Theme editor: selecting a size-chart block shows its group and tab.
 * - POLISH G-C (8 Oct): the product question and the chart pickers are <select>s (were pill clouds).
 * No layout reads in handlers; no scroll listeners.
 */
const parse = (el) => {
  try {
    return el ? JSON.parse(el.textContent) : null;
  } catch {
    return null;
  }
};
const num = (v) => (v === '' || v == null || Number.isNaN(parseFloat(v)) ? null : parseFloat(v));
const fill = (tpl, map) => String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
const ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
const JACKET = new Set(['chest', 'bust']);
const sizesFrom = (csv) =>
  String(csv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((label) => ({ value: label.split(' (')[0].trim(), label }));

class VmSizeGuide extends HTMLElement {
  connectedCallback() {
    const own = (sel) => this.querySelector(sel);
    this.data = parse(own('[data-vm-size-data]')) || {};
    this.links = parse(own('[data-vm-size-links]')) || {};
    this.copy = parse(own('[data-vm-fit-copy]')) || {};
    this.form = own('[data-fit-form]');
    this.shown = false;
    this.unit = this.field('unit') || 'in';
    this.gender = '';
    this.onChange = (e) => this.change(e);
    this.onSubmit = (e) => {
      e.preventDefault();
      this.run(true);
    };
    this.onClick = (e) => {
      if (e.target instanceof Element && e.target.closest('[data-fit-reset]')) this.reset();
    };
    this.onBlock = (e) => this.showBlock(e);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    if (this.form) this.form.addEventListener('submit', this.onSubmit);
    document.addEventListener('shopify:block:select', this.onBlock);
    this.preselect();
    this.adapt();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('shopify:block:select', this.onBlock);
  }

  el(sel) {
    return this.querySelector(sel);
  }

  field(key) {
    if (!this.form) return '';
    const el =
      this.form.querySelector(`[data-fit="${key}"]:checked`) ||
      this.form.querySelector(`[data-fit="${key}"]:not([type="radio"])`);
    return el ? el.value.trim() : '';
  }

  /** ?sizes=women|men|kids|unisex (from the PDP drawer) picks the charts' group; men/women also set the finder's gender. */
  preselect() {
    let g = '';
    try {
      g = new URLSearchParams(location.search).get('sizes') || '';
    } catch {
      g = '';
    }
    if (!['men', 'women', 'kids', 'unisex'].includes(g)) return;
    const radio = this.form && this.form.querySelector(`[data-fit="gender"][value="${g}"]`);
    if (radio) radio.checked = true;
    this.showGroup(g);
  }

  showGroup(g) {
    const r = this.querySelector(`input[data-vm-group="${g}"]`);
    if (r) r.checked = true;
  }

  /** Sizes for a product: the rendered table first, then the size JSON. */
  info(handle) {
    const table = [...this.querySelectorAll('.vm-st[data-handle]')].find((t) => t.dataset.handle === handle);
    if (table) {
      const rows = [...table.querySelectorAll('tbody tr[data-us]')];
      if (rows.length) {
        return {
          kind: table.dataset.kind,
          group: table.dataset.group || '',
          list: rows.map((r) => ({ us: r.dataset.us, num: r.dataset.num || '', tag: r.dataset.tag || '', min: num(r.dataset.min), max: num(r.dataset.max) })),
        };
      }
    }
    const p = (this.data.products || {})[handle];
    if (p && Array.isArray(p.sizes) && p.sizes.length) {
      // hand and foot rows carry more measures, height (kids) rows carry the age in [4]; only body rows have the women's numeric size in [4].
      const body = !['hand', 'foot', 'height'].includes(p.kind);
      return {
        kind: p.kind,
        group: p.g || '',
        list: p.sizes.map((s) => ({ us: s[0], tag: s[1] || '', min: num(s[2]), max: num(s[3]), num: body ? s[4] || '' : '' })),
      };
    }
    return null;
  }

  name(handle) {
    const l = this.links[handle];
    return (l && l.name) || ((this.data.products || {})[handle] || {}).name || handle;
  }

  group(handle) {
    const l = this.links[handle];
    if (l && l.g) return l.g;
    const i = this.info(handle);
    return (i && i.group) || '';
  }

  /** Product family from the size JSON ("jacket", "vest", "base", "pants", ...); the "goes bigger" hint stays inside one family. */
  family(handle) {
    const p = (this.data.products || {})[handle];
    return (p && p.fam) || '';
  }

  /** Show only the chosen gender's products; keep the same product (Eight -> Eight) when switching.
   *  POLISH G-C (8 Oct): the product question is a <select>; the option list is rebuilt from the full
   *  server list (iOS Safari ignores hidden options), so only this gender's products can be picked. */
  filterProducts(gender) {
    const select = this.form.querySelector('select[data-fit="product"]');
    if (!select) return;
    if (!this.productOpts) this.productOpts = [...select.querySelectorAll('[data-fit-opt]')];
    const opts = this.productOpts;
    if (!opts.length) return;
    const fits = (o) => o.dataset.group === gender || o.dataset.group === 'unisex';
    const any = opts.some(fits);
    const current = opts.find((o) => o.value === select.value && !o.disabled) || null;
    const family = current ? current.dataset.family : '';
    const keep = opts.filter((o) => (any ? fits(o) : true));
    for (const o of keep) {
      o.hidden = false;
      o.disabled = false;
    }
    select.replaceChildren(...keep);
    const next = keep.find((o) => o === current) || keep.find((o) => o.dataset.family === family) || keep[0];
    if (next) select.value = next.value;
  }

  /** Rebuild the usual-size list for the gender (women's letters carry numeric sizes). */
  usualSizes(gender) {
    const select = this.form.querySelector('[data-fit="usual"]');
    const list = sizesFrom(gender === 'women' ? this.copy.usual_women : this.copy.usual_men);
    if (!select || !list.length) return;
    const keep = select.value;
    const opts = [new Option(this.copy.placeholder || select.options[0]?.text || '', '')];
    for (const s of list) opts.push(new Option(s.label, s.value));
    select.replaceChildren(...opts);
    select.value = list.some((s) => s.value === keep) ? keep : '';
  }

  /** Labels and options that depend on gender and product; the hoodie question only applies to jackets. */
  adapt() {
    if (!this.form) return;
    const c = this.copy;
    const gender = this.field('gender') === 'women' ? 'women' : 'men';
    if (gender !== this.gender) {
      this.filterProducts(gender);
      this.usualSizes(gender);
      if (this.gender) this.showGroup(gender);
      this.gender = gender;
    }
    const info = this.info(this.field('product'));
    const kind = info ? info.kind : 'chest';
    const part = kind === 'hips' ? c.hips : kind === 'bust' ? c.bust : c.chest;
    const label = this.el('[data-fit-measure-label]');
    const hint = this.el('[data-fit-hint]');
    if (label && c.measure) label.textContent = fill(c.measure, { part });
    if (hint) hint.textContent = (kind === 'hips' ? c.hint_hips : kind === 'bust' ? c.hint_bust || c.hint_chest : c.hint_chest) || hint.textContent;
    const hoodie = this.el('[data-fit-hoodie]');
    if (hoodie) hoodie.hidden = !JACKET.has(kind);
  }

  change(e) {
    const t = e.target;
    if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
    // Keep the two unit toggles in step, and convert a typed measurement.
    if (t.matches('[data-fit="unit"], input[data-vm-unit]')) {
      const unit = t.value;
      for (const r of this.querySelectorAll('[data-fit="unit"], input[data-vm-unit]')) {
        if (r.value === unit) r.checked = true;
      }
      const input = this.form && this.form.querySelector('[data-fit="measure"]');
      const v = input ? num(input.value.replace(',', '.')) : null;
      if (input && v != null && unit !== this.unit) {
        input.value = unit === 'cm' ? String(Math.round(v * 2.54)) : String(Math.round((v / 2.54) * 2) / 2);
      }
      this.unit = unit;
    }
    if (this.form && this.form.contains(t)) {
      this.adapt();
      if (this.shown) this.run(false);
    }
  }

  error(msg, field) {
    const box = this.el('[data-fit-error]');
    if (box) box.textContent = msg || '';
    for (const key of ['usual', 'measure']) {
      const el = this.form.querySelector(`[data-fit="${key}"]`);
      if (el) el.toggleAttribute('aria-invalid', !!msg && (!field || field === key));
    }
    if (msg && field) {
      const el = this.form.querySelector(`[data-fit="${field}"]`);
      if (el) el.focus();
    }
  }

  run(submitted) {
    if (!this.form) return;
    const c = this.copy;
    const handle = this.field('product');
    const info = this.info(handle);
    if (!info || !info.list.length) return;
    const list = info.list;
    const jacket = JACKET.has(info.kind);
    const usual = this.field('usual');
    const unit = this.field('unit') || 'in';
    const raw = this.field('measure').replace(',', '.');
    const product = this.name(handle);
    this.unit = unit;

    let inches = null;
    let problem = '';
    if (raw) {
      const v = num(raw);
      inches = v == null ? null : unit === 'cm' ? v / 2.54 : v;
      if (inches == null || inches < 20 || inches > 70) problem = 'measure';
    } else if (!usual) {
      problem = 'usual';
    }
    if (problem) {
      // Never leave an old answer on screen next to changed inputs.
      this.clear();
      if (submitted) this.error(problem === 'measure' ? c.err_number : c.err_missing, problem);
      return;
    }
    this.error('');

    const order = Array.isArray(this.data.order) ? this.data.order : ORDER;
    let i = -1;
    let small = false;
    let reason = '';
    if (inches != null) {
      // First size whose range reaches the measurement; between two ranges this is the larger one.
      i = list.findIndex((s) => s.max != null && inches <= s.max);
      if (i === 0 && list[0].min != null && inches < list[0].min) small = true;
      const part = info.kind === 'hips' ? c.hips : info.kind === 'bust' ? c.bust : c.chest;
      reason = fill(c.reason_measure, { value: `${raw} ${unit}`, part, size: i >= 0 ? list[i].us : '' });
    } else {
      i = list.findIndex((s) => s.us === usual);
      if (i < 0 && order.indexOf(usual) >= 0 && order.indexOf(usual) < order.indexOf(list[0].us)) {
        i = 0;
        small = true;
      }
      reason = fill(c.reason_usual, { usual, size: i >= 0 ? list[i].us : '' });
    }

    // Hoodie (jackets) or a relaxed fit: one size up. Never both (a hoodie already adds the room).
    const hoodie = jacket && this.field('hoodie') === 'yes';
    const room = this.field('fit') === 'room';
    let j = i < 0 ? -1 : i + (hoodie || room ? 1 : 0);
    if (j >= list.length) j = -1;
    if (j >= 0 && j !== i) reason += ` ${fill(hoodie ? c.reason_hoodie : c.reason_room, { size: list[j].us })}`;

    // Measure before writing (no forced layout): on phones the result sits below the form.
    const box = submitted ? this.el('[data-fit-result]') : null;
    const below = box ? box.getBoundingClientRect().top > innerHeight * 0.6 : false;
    this.show({ handle, product, list, j, reason, small, bust: info.kind === 'bust', kind: info.kind });
    this.shown = true;
    if (box && below) {
      box.scrollIntoView({ block: 'start', behavior: document.documentElement.classList.contains('vm-rm') ? 'auto' : 'smooth' });
    }
  }

  show({ handle, product, list, j, reason, small, bust, kind }) {
    const c = this.copy;
    const set = (sel, text) => {
      const el = this.el(sel);
      if (!el) return null;
      el.textContent = text || '';
      el.hidden = !text;
      return el;
    };
    const empty = this.el('[data-fit-empty]');
    const out = this.el('[data-fit-out]');
    const actions = this.el('[data-fit-actions]');
    const select = this.el('[data-fit-select]');
    if (empty) empty.hidden = true;
    if (out) out.hidden = false;
    set('[data-fit-name]', product);

    const lead = this.el('[data-fit-lead]');
    let note = '';
    let href = '';
    if (j >= 0) {
      const size = list[j];
      if (lead) {
        const [before, after = ''] = String(c.lead || '{size}').split('{size}');
        const strong = document.createElement('strong');
        strong.textContent = size.num ? `${size.us} (${size.num})` : size.us;
        lead.replaceChildren(before, strong, after);
      }
      set('[data-fit-reason]', reason);
      // POLISH G-C (R4-04): the tag line only when the tag differs from the US size.
      const tagDiffers = size.tag && String(size.tag).trim().toUpperCase() !== String(size.us).trim().toUpperCase();
      set('[data-fit-tag]', tagDiffers && kind !== 'hand' ? fill(c.tag, { tag: size.tag }) : '');
      if (small) note = fill(c.small, { size: size.us, product });
      else if (bust) note = c.women;
      const link = this.links[handle];
      if (link && link.url) {
        const id = link.v && link.v[size.us];
        // POLISH G-C (R4-03): &size= tells vm-pdp.js to arrive with this size chosen (nothing is preselected otherwise).
        href = id ? `${link.url}${link.url.includes('?') ? '&' : '?'}variant=${id}&size=${encodeURIComponent(size.us)}` : link.url;
      }
    } else {
      const max = list[list.length - 1].us;
      if (lead) lead.textContent = fill(c.over, { product, max });
      set('[data-fit-reason]', '');
      set('[data-fit-tag]', '');
      // Suggest another product of the same gender, kind and family (jacket -> jacket, never a vest) that goes bigger.
      const order = Array.isArray(this.data.order) ? this.data.order : ORDER;
      const g = this.group(handle);
      const fam = this.family(handle);
      const sameKind = (k) => (JACKET.has(kind) ? JACKET.has(k) : k === kind);
      for (const other of Object.keys(this.links)) {
        if (other === handle || this.group(other) !== g) continue;
        if (fam && this.family(other) && this.family(other) !== fam) continue;
        const o = this.info(other);
        if (!o || !sameKind(o.kind)) continue;
        const omax = o.list[o.list.length - 1].us;
        if (order.indexOf(omax) > order.indexOf(max)) {
          note = fill(c.other, { other: this.name(other), max: omax });
          href = this.links[other].url;
          break;
        }
      }
    }
    set('[data-fit-note]', note);
    if (actions) actions.hidden = false;
    if (select) {
      select.hidden = !href;
      if (href) select.setAttribute('href', href);
    }
  }

  clear() {
    const empty = this.el('[data-fit-empty]');
    const out = this.el('[data-fit-out]');
    const actions = this.el('[data-fit-actions]');
    if (empty) empty.hidden = false;
    if (out) out.hidden = true;
    if (actions) actions.hidden = true;
  }

  reset() {
    const first = this.form && (this.form.querySelector('[data-fit]:checked') || this.form.querySelector('[data-fit]'));
    if (!first) return;
    const reduce = document.documentElement.classList.contains('vm-rm');
    this.form.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    first.focus({ preventScroll: true });
  }

  showBlock(e) {
    const id = e.detail && e.detail.blockId;
    if (!id) return;
    const tab = [...this.querySelectorAll('[data-vm-tab]')].find((r) => r.dataset.vmTab === id);
    if (tab instanceof HTMLOptionElement) tab.selected = true;
    else if (tab) tab.checked = true;
    const panel = [...this.querySelectorAll('[data-block]')].find((p) => p.dataset.block === id);
    const group = panel && panel.closest('[data-group]');
    if (group) this.showGroup(group.dataset.group);
  }
}

if (!customElements.get('vm-size-guide')) customElements.define('vm-size-guide', VmSizeGuide);
